import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, ReferenceArea, ReferenceLine } from 'recharts';
import { convertSingleValue, currencyToSymbol } from '../utils/commonUtils';

const MIN_ZOOM = 5;
const DEFAULT_ZOOM = { x1: null, y1: null, x2: null, y2: null };

const ManualScatterGraph = ({ data, height = 500 }) => {
    const unitX = data.unit[0];
    const unitY = data.unit[1];
    
    const color = [
        { fill: "rgba(45, 186, 177, .5)", edge: "rgba(45, 186, 77, .5)"},
        { fill: 'rgba(145, 186, 250, 1)', edge: 'rgba(80, 140, 200, 1)' }, // 青
        // { fill: 'rgba(90, 195, 90, 1)', edge: 'rgba(50, 110, 50, 1)' }, // 緑
        { fill: 'rgba(225, 97, 50, 1)', edge: 'rgba(255, 120, 90, 1)' }, // オレンジ
        { fill: 'rgba(147, 112, 219, 1)', edge: 'rgba(75, 0, 130, 1)' },  // 紫
        { fill: 'rgba(255, 235, 59, 1)', edge: 'rgba(251, 192, 45, 1)' }, // 黄色   
    ];

    // 軸の値を兆や億に変換するフォーマッター
    const formatTick = (tick) => convertSingleValue(tick);

    const [filteredData, setFilteredData] = useState(null);
    const [zoomArea, setZoomArea] = useState(DEFAULT_ZOOM);
    const [isZooming, setIsZooming] = useState(false);
    const [clickTimeout, setClickTimeout] = useState(null);
    const [xRange, setXRange] = useState(0);
    const [yRange, setYRange] = useState(0);

    useEffect(() => {
        if (data) {
            setFilteredData(data);
        }
    }, [data])

    // リセット用関数
    const handleZoomOut = () => {
        setFilteredData(data);
        setZoomArea(DEFAULT_ZOOM);
    };

    const handleMouseDown = (e) => {
        if (clickTimeout) {
            clearTimeout(clickTimeout);  // ダブルクリックを検出
            setClickTimeout(null);
            handleZoomOut();  // ダブルクリック時はズームアウト
        } else {
            const newTimeout = setTimeout(() => {
                setIsZooming(true);
                const { xValue, yValue } = e || {};
                setZoomArea({ x1: xValue, y1: yValue, x2: xValue, y2: yValue });
                setClickTimeout(null);
            }, 200);  // 200ms以内に2回クリックされたらダブルクリックとみなす
            setClickTimeout(newTimeout);
        }
    };

    const handleMouseMove = (e) => {
        if (isZooming) {
            setZoomArea((prev) => ({ ...prev, x2: e?.xValue, y2: e?.yValue }));
        }
    };

    const handleMouseUp = (e) => {
        if (isZooming) {
            setIsZooming(false);
            let { x1, y1, x2, y2 } = zoomArea;

            // x1 <= x2, y1 <= y2 を確保
            if (x1 > x2) [x1, x2] = [x2, x1];
            if (y1 > y2) [y1, y2] = [y2, y1];

            if (x2 - x1 >= MIN_ZOOM && y2 - y1 >= MIN_ZOOM) {
                const dataPointsInRange = filterDataInRange(filteredData, x1, x2, y1, y2);
                setFilteredData(dataPointsInRange);
            }
            setZoomArea(DEFAULT_ZOOM);
        } else if (clickTimeout) {
            clearTimeout(clickTimeout);
            setClickTimeout(null);
        }
    };

    const filterDataInRange = (data, x1, x2, y1, y2) => {
        const filteredDataPoints = data.data.filter(
            (d) => d.x >= x1 && d.x <= x2 && d.y >= y1 && d.y <= y2
        );
    
        const filteredCompanies = data.companies.filter(company =>
            filteredDataPoints.some(dataPoint => dataPoint.companyName === company.companyName)
        );

        return {
            data: filteredDataPoints,
            unit: data.unit,
            name: data.name,
            graphType: data.graphType,
            companies: filteredCompanies
        };
    };

    const CustomMarker = (props) => {
        const { cx, cy, fill } = props;
        const size = 16;
        const strokeColor = color[0]['edge'];
        const strokeWidth = 1;
        return (
            <circle
                cx={cx}
                cy={cy}
                r={size / 2}
                fill={fill}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
            />
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const currencyX = unitX === 'value' ? currencyToSymbol(payload[0].payload.currency) : '';
            const currencyY = unitY === 'value' ? currencyToSymbol(payload[1].payload.currency) : '';

            return (
                <div className="custom-tooltip px-3 py-2 border border-gray-300 bg-gray-600 rounded shadow text-white">
                    <div>
                        <div>{`${payload[0].payload.companyName}`}</div> 
                        <div className='flex'>
                            {`${data.name[0]}: ${convertSingleValue(payload[0].payload.x)}${currencyX} (${payload[0].payload.xTiming})`}
                        </div>
                        <div className='flex'>
                            {`${data.name[1]}: ${convertSingleValue(payload[1].payload.y)}${currencyX} (${payload[1].payload.yTiming})`}
                        </div>
                        
                    </div>
                </div>
            );
        }
        return null;
    };

    useEffect(() => {
        if (filteredData?.data?.length > 0) {
            const xValues = filteredData.data.map(d => d.x);
            const yValues = filteredData.data.map(d => d.y);
            const xMin = Math.min(...xValues);
            const xMax = Math.max(...xValues);
            const yMin = Math.min(...yValues);
            const yMax = Math.max(...yValues);

            setXRange((xMax - xMin) * 0.1);
            setYRange((yMax - yMin) * 0.1);
        }
    }, [filteredData]);

    if (!filteredData) {
        return <div>Loading ...</div>
    }
    return (
        <div className="relative">
            <div
                className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded-full flex items-center justify-center cursor-pointer absolute top-2 right-8 z-10" // z-indexを追加
                onClick={(e) => {
                    handleZoomOut();
                }}
            >
                <i className="fa-solid fa-arrows-rotate"></i>
            </div>
            <ResponsiveContainer width="100%" height={height}>
                <ScatterChart
                    margin={{ top: 40, right: 30, left: 30, bottom: 40 }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <ReferenceLine x={0} stroke="grey" />
                    <ReferenceLine y={0} stroke="grey" />

                    <XAxis
                        dataKey="x"
                        type="number"
                        tickFormatter={isZooming ? () => '' : formatTick}
                        domain={[`dataMin - ${xRange}`, `dataMax + ${xRange}`]}
                        tick={{ fontSize: 12, fill: '#000' }}
                    >
                        <Label 
                            value={data.name[0]}
                            position="bottom" 
                            offset={10}
                            style={{ textAnchor: 'middle', fill: '#000' }}
                        />
                    </XAxis>
                    
                    <YAxis
                        dataKey="y"
                        type="number"
                        tickFormatter={isZooming ? () => '' : formatTick}
                        domain={[`dataMin - ${yRange}`, `dataMax + ${yRange}`]}
                        tick={{ fontSize: 12, fill: '#000' }}
                    >
                        <Label 
                            value={data.name[1]}
                            position="top" 
                            offset={25} 
                            angle={0}
                            style={{ textAnchor: 'middle', fill: '#000' }}
                        />
                    </YAxis>

                    {!isZooming && (
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: 'rgba(200, 200, 200, 0.5)' }}
                        />
                    )}

                    {isZooming && (
                        <ReferenceArea
                            x1={zoomArea?.x1}
                            x2={zoomArea?.x2}
                            y1={zoomArea?.y1}
                            y2={zoomArea?.y2}
                        />
                    )}
                    
                    <Scatter
                        data={filteredData.data}
                        fill={color[0]['fill']}
                        shape={<CustomMarker />}
                    />
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ManualScatterGraph;
